// MATERIAL-UI
import { Modal, CircularProgress, IconButton } from "@mui/material";
import React from "react";

//REDUX
import { useAppDispatch } from "src/store/store";
import {
  useResendWithdrawalEmailCodeMutation,
  useProcessWithdrawalMutation,
  useCheckIsUserLockedQuery,
  WithdrawAddressSeparateRes,
  useGetMinimumWithdrawalAmountMutation,
} from "src/store/features/api/generated";

// STYLES
import {
  StyledMainContainer,
  StyledModalTitle,
  StyledNextButton,
  StyledTitleContainer,
  StyledWithdrawalBox,
} from "../common/styles/CryptoWithdrawalStyled";

// COMPONENTS
import { CryptoWithdrawalInputDetails } from "./CryptoWithdrawalInputDetails";
import { CryptoWithdrawalNotabene } from "./CryptoWithdrawalNotabene";
import { CryptoWithdrawalStageReview } from "./CryptoWithdrawalStageReview";
import { CryptoWithdrawalStageVerification } from "./CryptoWithdrawalStageVerification";
import { CryptoWithdrawalStageCompleted } from "./CryptoWithdrawCompleted";
import CryptoWithdrawalTrBackfill from "./CryptoWithdrawalTrBackfill";
import { useTrEnabled } from "src/utils/hooks/useTrEnabled";
import { StyledError } from "../Modal/Withdraw/Error";

// ICONS
import BackIcon from "src/icons/BackIcon";
import { Close } from "@mui/icons-material";

// REDUX
import {
  setCryptoWithdrawalStage,
  resetWithdrawalThunk,
  inputStageThunk,
  setError,
  useWithdrawalSlice,
  setIsUnderMaintenance,
  resetNotabeneState,
  setIsCryptoWithdrawalModalOpen,
  setWithdrawalPreparedToken,
} from "src/store/features/withdrawal.slice";
import { useGetNotabeneTransferQuery } from "src/store/features/api/notabene";
import { usePrepareWithdrawalMutation } from "src/store/features/api/generated";
import { useUserSlice } from "src/store/features/user.slice";

// AMPLITUTDE
import { trackEvent } from "src/Amplitude";

// UTILS
import { getSessionItem } from "src/utils/SessionManagement";
import { useEffect } from "react";

// HOOKS
import usePermission from "src/utils/hooks/usePermission";
import useTransactionDisclosure, {
  type DisclosureMetadata,
} from "src/utils/hooks/useTransactionDisclosure";
import { TransactionDisclosureModal } from "src/components/disclosures/TransactionDisclosureModal";
import useTransmitRisk from "src/utils/hooks/useTransmitRisk";

/**************
 * INTERFACES *
 **************/
export interface Token {
  code: string;
  displayName: string;
  available: number;
  logoUrl?: string;
  memo?: boolean;
  format?: string;
  totalValue?: number;
  price?: number;
  value?: number;
}

interface CryptoWithdrawalModalProps {
  cryptoAddresses: WithdrawAddressSeparateRes[];
}

interface IsButtonDisabledProps {
  cryptoWithdrawalStage: string;
  isCheckingMinWithdrawalLoading: boolean;
  isProcessing: boolean;
  is2FAEnabled?: boolean;
  isEmailCodeSuccess: boolean;
  emailSending?: boolean;
  code2fa: string | null;
  isGuest?: boolean;
}

/********************
 * HELPER FUNCTIONS *
 ********************/
/**
 * Function to check if the button should be disabled
 *
 * @param {IsButtonDisabledProps} IsButtonDisabledProps - Props for the isButtonDisabled function
 * @returns {boolean} - Returns a boolean value to disable the button
 */
const isButtonDisabled = ({
  isCheckingMinWithdrawalLoading,
  isProcessing,
  is2FAEnabled,
  cryptoWithdrawalStage,
  isEmailCodeSuccess,
  emailSending,
  code2fa,
  isGuest,
}: IsButtonDisabledProps) => {
  return (
    isCheckingMinWithdrawalLoading ||
    isProcessing ||
    !is2FAEnabled ||
    emailSending ||
    isGuest ||
    (cryptoWithdrawalStage === "Verification" && !isEmailCodeSuccess) ||
    (cryptoWithdrawalStage === "Verification" && code2fa?.length !== 6)
  );
};

/**
 * CryptoWithdrawalModal component
 *
 * @param {CryptoWithdrawalModalProps} cryptoAddresses - Props for the CryptoWithdrawalModal
 * @returns {JSX.Element} - Returns the JSX element for the CryptoWithdrawalModal
 */
export function CryptoWithdrawalModal({ cryptoAddresses }: CryptoWithdrawalModalProps) {
  const dispatch = useAppDispatch();
  const currentEmail = getSessionItem("email");

  const { isGuest } = usePermission();
  const { executeTransmitRisk } = useTransmitRisk();
  // Master TR kill-switch — when off, skip both the TrBackfill stage AND
  // the Notabene SDK mount stage. Goes straight from Verification to
  // Review. Backend withdrawal.service is also gated, so the legacy
  // (pre-TR) Greenwich/Fireblocks dispatch path runs end-to-end.
  const trOn = useTrEnabled("OUTBOUND");

  /***************
   * REDUX STATE *
   ***************/
  const {
    selectedToken,
    walletAddress,
    displayWalletAddress,
    cryptoWithdrawalStage,
    memo,
    withdrawalAmount,
    error,
    isCryptoWithdrawalModalOpen,
    code2fa,
    emailCode,
    isEmailCodeSuccess,
    isUnderMaintenance,
    notabeneTxId,
    notabeneStatus,
    notabenePiiSubmitted,
    withdrawalPreparedToken,
  } = useWithdrawalSlice();

  const { is2FAEnabled } = useUserSlice();

  /*************
   * STATE VARIABLES *
   *************/
  const [showDisclosureModal, setShowDisclosureModal] = React.useState<boolean>(false);
  // D8 — VASP mismatch acknowledgement. Set from CryptoWithdrawalStageReview
  // when the persisted Notabene transfer reports counterpartyMismatch=true.
  // Withdraw button is gated on this when the flag is set; no-op otherwise.
  const [mismatchAcknowledged, setMismatchAcknowledged] = React.useState<boolean>(false);
  // Double-submit guard: ref blocks re-entry synchronously, state disables the
  // button for the full submit span (isProcessing misses the transmit-risk call).
  const isSubmittingRef = React.useRef(false);
  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);

  /*************
   * TRANSACTION-SPECIFIC DISCLOSURE *
   *************/
  // Don't auto-open disclosure - we'll control it manually
  const {
    transactionDisclosure,
    requiresDisclosure,
    isLoading: isDisclosureLoading,
    disclosureMetadata: acceptedDisclosureMetadata,
    disclosurePayload,
    modalProps,
    closeModal: handleDisclosureClose,
  } = useTransactionDisclosure((disclosureId: string, metadata?: DisclosureMetadata) => {
    // The modal only opens from the Withdraw press, so accepting always continues.
    processWithdrawalRequest(metadata);
    setShowDisclosureModal(false);
  }, "crypto_withdrawal", "withdrawal-disclosure");

  // Handle disclosure modal close
  const handleDisclosureModalClose = React.useCallback(() => {
    // If disclosure is closed without accepting, just close the disclosure modal
    setShowDisclosureModal(false);
    handleDisclosureClose();
  }, [handleDisclosureClose]);

  // Override the modal open state to control it manually
  const disclosureModalProps = React.useMemo(
    () => ({
      ...modalProps,
      open: showDisclosureModal && !!transactionDisclosure,
      onClose: () => {
        handleDisclosureModalClose();
      },
    }),
    [modalProps, showDisclosureModal, transactionDisclosure, handleDisclosureModalClose],
  );

  /*************
   * API CALLS *
   *************/
  const [sendEmail, { isLoading: emailSending }] = useResendWithdrawalEmailCodeMutation();
  const [processWithdrawal, { isLoading: isProcessing }] = useProcessWithdrawalMutation();
  const [prepareWithdrawal, { isLoading: isPreparing }] = usePrepareWithdrawalMutation();
  const [getMinWithdrawalAmount, { isLoading: isCheckingMinWithdrawalLoading }] =
    useGetMinimumWithdrawalAmountMutation();
  const {
    data: isLocked,
    refetch,
    error: errorIsLocked,
  } = useCheckIsUserLockedQuery({ fiat: false }, { skip: isGuest });

  /*************************
   * BUTTON CLICK HANDLERS *
   *************************/
  // Stage 1 → 2: validate min withdrawal, then send the email code and move
  // the user to the Verification stage. We require the user to authenticate
  // (email + 2FA) BEFORE creating the Notabene transfer so that a
  // session-only attacker can't fire the Notabene side-effects (PII to
  // counterparties, compliance review) without passing 2FA.
  const handleInputClick = async () => {
    refetch();
    if (isLocked) {
      dispatch(
        setError(
          "You have recently submitted too many failed withdrawal attempts. Please try again in a few minutes.",
        ),
      );
      return;
    }
    dispatch(setError(""));
    trackEvent(
      "Crypto Withdrawal Next Button",
      { stage: cryptoWithdrawalStage },
      "Crypto Withdrawal Modal",
    );
    if (!walletAddress || !selectedToken || !withdrawalAmount || withdrawalAmount <= 0) {
      dispatch(setError("Please provide valid withdrawal details."));
      return;
    }
    // Starting a fresh withdrawal from InputDetails — drop any Notabene state
    // left over from a previous attempt. Without this, a stale
    // `notabenePiiSubmitted` + `notabeneTxId` (e.g. from an earlier withdrawal
    // whose modal was closed mid-flight, which handleClose deliberately
    // preserves) makes handleVerificationClick take the "resume" shortcut and
    // skip the Notabene step entirely — so a brand-new, never-attested address
    // sails past Travel Rule (the JonLow CT01210464 case). A genuine resume
    // reopens at the Notabene/Review/waiting stage, never InputDetails, so this
    // only ever clears state for an actual fresh start. Done AFTER input
    // validation so a mis-clicked Next on an incomplete form doesn't discard a
    // resumable transfer.
    dispatch(resetNotabeneState());
    try {
      await getMinWithdrawalAmount({
        minimumWithdrawalDto: {
          token: selectedToken.code,
          amount: withdrawalAmount,
        },
      }).unwrap();
      await sendEmail({
        sendWithdrawalEmailCodeDto: {
          cryptoAddress: walletAddress,
          token: selectedToken.code,
          amount: withdrawalAmount.toString(),
          type: "Crypto",
        },
      }).unwrap();
      dispatch(setCryptoWithdrawalStage("Verification"));
    } catch (error: any) {
      dispatch(setError(error?.data?.message || error?.message || "Failed to start withdrawal."));
    }
  };

  // Stage 2 → 3: hand the codes to the backend for validation and grab a
  // withdrawal-prepared token. From here on the Notabene calls + final
  // submit carry that token; if email or 2FA is wrong, we never reach
  // Notabene at all.
  const handleVerificationClick = async () => {
    dispatch(setError(""));
    trackEvent(
      "Crypto Withdrawal Verification Next",
      { stage: cryptoWithdrawalStage },
      "Crypto Withdrawal Modal",
    );
    if (!walletAddress || !selectedToken || !withdrawalAmount || !emailCode || !code2fa) {
      dispatch(setError("Please enter your email code and 2FA code."));
      return;
    }
    try {
      const result = await prepareWithdrawal({
        prepareWithdrawalDto: {
          asset: selectedToken.code,
          amount: withdrawalAmount,
          destinationAddress: walletAddress,
          emailCode,
          code: code2fa,
        },
      }).unwrap();
      dispatch(
        setWithdrawalPreparedToken({ token: result.token, expiresAt: result.expiresAt }),
      );
      // Resume case: the Notabene SDK has already been completed in a prior
      // session (or pre-prepared-token deployment). Re-verifying just
      // re-issues a fresh prepared token, so we can skip Notabene and go
      // straight to Review.
      if (notabenePiiSubmitted && notabeneTxId) {
        dispatch(setCryptoWithdrawalStage("Review"));
      } else if (!trOn || result.travelRuleRequired === false) {
        // Skip TR and go straight to Review when either:
        //  - the master TR flag is off (legacy pre-TR dispatch handles the
        //    rest; the backend verify gate is also flag-aware), or
        //  - the backend's per-withdrawal verdict says TR isn't required for
        //    THIS withdrawal (US-entity under the de-minimis USD threshold).
        //    Strictly `=== false`: an older backend omits the field, and
        //    undefined must fall through to the TR stages (fail safe). The
        //    backend stamped the skip on the prepared token, so the process
        //    gate honours the same verdict.
        dispatch(setCryptoWithdrawalStage("Review"));
      } else {
        // D4 — if the selected address still needs Travel-Rule data,
        // route through the TrBackfill stage before the Notabene SDK mount.
        // The TrBackfill submit handler advances to "Notabene" on success.
        const selected = cryptoAddresses.find((a) => a.address === walletAddress) as
          | (typeof cryptoAddresses[number] & { travelRuleStatus?: string })
          | undefined;
        const nextStage =
          selected?.travelRuleStatus === "requires_backfill" ? "TrBackfill" : "Notabene";
        dispatch(setCryptoWithdrawalStage(nextStage));
      }
    } catch (error: any) {
      dispatch(
        setError(
          error?.data?.message ||
            error?.message ||
            "Verification failed. Please check your codes and try again.",
        ),
      );
    }
  };

  // Stage 4 → 5: the actual withdrawal submission. Uses the
  // withdrawal-prepared token (set during the Verification stage) instead
  // of raw codes — the token is single-use and consumed atomically server-side.
  const handleReviewClick = async () => {
    // Resume safety net: if the user reached Review without a
    // withdrawal-prepared token (stale persisted state from before the
    // prepared-token deployment, or a hydrated in-flight transfer), the
    // backend would reject the submit because raw codes are intentionally
    // empty. Send them back to Verification to issue a fresh token; on
    // success, handleVerificationClick will skip Notabene and return them
    // straight here.
    if (!withdrawalPreparedToken) {
      dispatch(
        setError(
          "Please re-enter your verification codes to continue with this withdrawal.",
        ),
      );
      dispatch(setCryptoWithdrawalStage("Verification"));
      return;
    }
    // Still resolving — requiresDisclosure === false means "unknown", not "not required"
    if (isDisclosureLoading) {
      return;
    }
    // Disclosure gate — once accepted today, fall through and submit instead of re-prompting
    if (requiresDisclosure && !acceptedDisclosureMetadata) {
      if (!showDisclosureModal) {
        setShowDisclosureModal(true);
      }
      return;
    }
    await processWithdrawalRequest(undefined);
  };

  // Process withdrawal (after disclosure is accepted or when no disclosure required)
  const processWithdrawalRequest = async (disclosureMetadata?: DisclosureMetadata) => {
    if (isSubmittingRef.current) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      dispatch(setError(""));
      trackEvent(
        "Processing Withdrawal Request",
        { email: currentEmail, notabeneTxId },
        "Crypto Withdrawal Modal",
      );

      // Capture a fresh transaction risk snapshot immediately before submitting,
      // matching fiat withdrawal behaviour. The result is stored in riskRecommendations
      // on the backend; the withdrawal service reads from there at submission time.
      await executeTransmitRisk("transaction", undefined, {
        amount: withdrawalAmount,
        currency: selectedToken?.code,
        reason: "crypto-withdrawal",
      }).catch(() => {
        // Non-blocking — backend treats a missing risk record as high-risk automatically.
      });
      const timezone =
        typeof Intl !== "undefined" ? Intl.DateTimeFormat().resolvedOptions().timeZone : "UTC";
      const disclosureFields = (disclosurePayload || disclosureMetadata) && {
        disclosureRequestDateTime:
          disclosurePayload?.disclosureRequestDateTime ||
          disclosureMetadata?.disclosureRequestDateTime,
        disclosureAcceptanceDateTime:
          disclosurePayload?.disclosureAcceptanceDateTime ||
          disclosureMetadata?.disclosureAcceptanceDateTime,
        timezone: timezone,
      };
      await processWithdrawal({
        processWithdrawalDto: {
          address: walletAddress || "",
          token: selectedToken?.code || "",
          amount: withdrawalAmount,
          // Codes left empty — backend skips the raw-code check when a valid
          // withdrawal-prepared token is supplied, since that token is itself
          // proof the user passed email + 2FA at the start of the flow.
          emailCode: "",
          code: "",
          // Include Notabene transfer ID for Travel Rule compliance
          ...(notabeneTxId ? { notabeneTxId } : {}),
          // Withdrawal-prepared token (issued by /withdrawal/prepare).
          ...(withdrawalPreparedToken ? { withdrawalToken: withdrawalPreparedToken } : {}),
          ...(disclosureFields ?? {}),
        } as any,
      })
        .unwrap()
        .then(() => {
          dispatch(setCryptoWithdrawalStage("Completed"));
          // Reset Notabene state after successful withdrawal
          dispatch(resetNotabeneState());
        })
        .catch((error) => {
          // Token expired / already consumed / not yet issued — the backend
          // returns 403 with this message from `verifyWithdrawalPreparedToken`
          // / `consumePreparedWithdrawalToken`. (Older deployments returned
          // 401; we still match it so a half-deployed cluster doesn't strand
          // the user.) Drop the stale token and bounce the user to Verification
          // so they can re-issue.
          const status = error?.status ?? error?.data?.statusCode;
          const message: string = error?.data?.message || error?.message || "";
          const tokenInvalid =
            status === 403 ||
            status === 401 ||
            /withdrawal-prepared token/i.test(message) ||
            /Complete the verification step first/i.test(message);
          if (tokenInvalid) {
            dispatch(
              setWithdrawalPreparedToken({ token: null, expiresAt: null }),
            );
            dispatch(
              setError(
                "Your verification has expired. Please re-enter your codes to continue.",
              ),
            );
            dispatch(setCryptoWithdrawalStage("Verification"));
            return;
          }
          // RTK Query errors carry the server message on error.data.message.
          dispatch(
            setError(message || "Failed to process withdrawal. Please try again."),
          );
        });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    // Preserve the in-flight Notabene flow if the user has already submitted
    // their PII and we're waiting for counterparty/compliance authorization.
    // Closing the modal in this state should let them come back to the same
    // "Waiting for verification…" screen with their token/address/amount and
    // existing transfer intact, rather than starting over.
    const verificationInFlight =
      notabenePiiSubmitted &&
      !!notabeneTxId &&
      notabeneStatus !== "rejected" &&
      cryptoWithdrawalStage !== "Completed";

    if (verificationInFlight) {
      dispatch(setIsCryptoWithdrawalModalOpen(false));
      return;
    }

    dispatch(resetWithdrawalThunk());
    dispatch(resetNotabeneState());
  };

  const handleBackButton = () => {
    // Going back from Notabene or Review means abandoning the
    // withdrawal-prepared token and the in-flight Notabene transfer — the
    // user will need to re-enter their codes (and either reuse or recreate
    // the Notabene transfer) when they advance again. Clear those fields
    // so we don't leak orphaned tokens / mismatched transfers.
    if (cryptoWithdrawalStage === "Notabene" || cryptoWithdrawalStage === "Review") {
      dispatch(resetNotabeneState());
      dispatch(setCryptoWithdrawalStage("InputDetails"));
      return;
    }
    if (cryptoWithdrawalStage === "Verification" || cryptoWithdrawalStage === "TrBackfill") {
      // The Verification and TrBackfill stages haven't issued a prepared
      // token or initiated Notabene yet (those happen on Verification's
      // Next click and on TrBackfill's submit, respectively), so we can
      // simply step back to InputDetails without resetting Notabene state.
      dispatch(setCryptoWithdrawalStage("InputDetails"));
      return;
    }
    dispatch(inputStageThunk());
  };

  const handleNotabeneCompletion = (notabeneTxIdFromComponent: string) => {
    // Notabene completion callback - proceed to Review stage
    trackEvent(
      "Notabene Completion - Proceeding to Review",
      { email: currentEmail, notabeneTxId: notabeneTxIdFromComponent },
      "Crypto Withdrawal Modal",
    );
    dispatch(setCryptoWithdrawalStage("Review"));
  };

  const handleModalProcess = (cryptoWithdrawalStage: string) => {
    if (cryptoWithdrawalStage === "InputDetails") handleInputClick();
    else if (cryptoWithdrawalStage === "Review") handleReviewClick();
    else if (cryptoWithdrawalStage === "Verification") handleVerificationClick();
    // Note: Notabene stage doesn't need a button handler as it auto-advances on completion
  };

  /*******************
   * USE EFFECTS *
   *******************/
  useEffect(() => {
    if (
      errorIsLocked &&
      errorIsLocked.message?.includes("Withdrawals are currently under maintenance on Portal")
    ) {
      dispatch(setIsUnderMaintenance(true));
      dispatch(setError(errorIsLocked.message));
    }
  }, [errorIsLocked]);

  /*******************
   * STATE VARIABLES *
   *******************/
  // D8 — pull the transfer-level counterpartyMismatch flag so we can gate
  // the Withdraw button on the user's acknowledgement. Skip when no txId
  // (legacy flows) — `data` is undefined in that case and the gate noops.
  const { data: notabeneTransfer } = useGetNotabeneTransferQuery(notabeneTxId ?? "", {
    skip: !notabeneTxId,
  });
  const counterpartyMismatch = Boolean(notabeneTransfer?.counterpartyMismatch);

  const disabled =
    isButtonDisabled({
      cryptoWithdrawalStage,
      isCheckingMinWithdrawalLoading,
      isProcessing: isProcessing || isSubmitting,
      is2FAEnabled,
      isEmailCodeSuccess,
      emailSending,
      code2fa,
      isGuest,
    }) ||
    isPreparing ||
    (cryptoWithdrawalStage === "Review" && counterpartyMismatch && !mismatchAcknowledged);

  // Stage order: InputDetails -> Verification -> Notabene -> Review -> Completed.
  // "Withdraw" is the final action on the Review stage; the Verification
  // stage is just submitting 2FA to advance, so it shows "Next".
  const buttonText = emailSending
    ? "Sending verification email"
    : isPreparing
    ? "Verifying…"
    : cryptoWithdrawalStage === "Review"
    ? "Withdraw"
    : "Next";

  const isLoading =
    isCheckingMinWithdrawalLoading || isProcessing || isSubmitting || emailSending || isPreparing;

  return (
    <>
      <Modal open={isCryptoWithdrawalModalOpen} onClose={handleClose}>
        <StyledWithdrawalBox>
          <StyledTitleContainer>
            {cryptoWithdrawalStage !== "InputDetails" && cryptoWithdrawalStage !== "Completed" && (
              <IconButton
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2, cursor: "pointer" }}
                onClick={handleBackButton}>
                <BackIcon width={18} height={18} />
              </IconButton>
            )}
            <StyledModalTitle sx={{ flexGrow: 1, textAlign: "center" }}>
              {cryptoWithdrawalStage === "Completed" ? "Request Completed" : "Withdraw"}
            </StyledModalTitle>
            <Close onClick={handleClose} style={{ cursor: "pointer" }} />
          </StyledTitleContainer>
          <StyledMainContainer id="crypto-withdrawal-modal">
            {/* Stage 1: Entering withdrawal request details */}
            {cryptoWithdrawalStage === "InputDetails" && !isUnderMaintenance && (
              <CryptoWithdrawalInputDetails
                token={selectedToken}
                withdrawalAddress={walletAddress}
                memo={memo}
                cryptoAddresses={cryptoAddresses}
              />
            )}

            {/* Stage 1.5 (D4): Travel-Rule backfill, only when the address
                requires it. Self-advances to "Notabene" on submit. */}
            {cryptoWithdrawalStage === "TrBackfill" && !isUnderMaintenance && (
              <CryptoWithdrawalTrBackfill />
            )}

            {/* Stage 2: Notabene Travel Rule Verification */}
            {cryptoWithdrawalStage === "Notabene" && !isUnderMaintenance && (
              <CryptoWithdrawalNotabene
                token={selectedToken}
                withdrawalAddress={walletAddress}
                withdrawalAmount={withdrawalAmount}
                onCompletion={handleNotabeneCompletion}
              />
            )}

            {/* Stage 3: Review */}
            {cryptoWithdrawalStage === "Review" && !isUnderMaintenance && (
              <CryptoWithdrawalStageReview
                token={selectedToken}
                withdrawalAddress={displayWalletAddress}
                memo={memo}
                withdrawalAmount={withdrawalAmount}
                onMismatchAckChange={setMismatchAcknowledged}
              />
            )}

            {/* Stage 4: Authenticating the user */}
            {cryptoWithdrawalStage === "Verification" && !isUnderMaintenance && (
              <CryptoWithdrawalStageVerification
                token={selectedToken}
                withdrawalAddress={walletAddress}
                withdrawalAmount={withdrawalAmount}
                isLoading={isLoading}
              />
            )}

            {/* Stage 5: Completion*/}
            {cryptoWithdrawalStage === "Completed" && !isUnderMaintenance && (
              <CryptoWithdrawalStageCompleted
                token={selectedToken}
                withdrawalAddress={displayWalletAddress}
                memo={memo}
                withdrawalAmount={withdrawalAmount}
              />
            )}
            {error && <StyledError>{error}</StyledError>}
            {/* Hide button during Notabene + TrBackfill stages — both render
                their own controls. */}
            {cryptoWithdrawalStage !== "Completed" &&
              cryptoWithdrawalStage !== "Notabene" &&
              cryptoWithdrawalStage !== "TrBackfill" &&
              !isUnderMaintenance && (
              <StyledNextButton
                onClick={() => handleModalProcess(cryptoWithdrawalStage)}
                fullWidth
                disabled={disabled}>
                {buttonText}
                {isLoading && <CircularProgress size={20} style={{ marginLeft: "10px" }} />}
              </StyledNextButton>
            )}
          </StyledMainContainer>
        </StyledWithdrawalBox>
      </Modal>

      {/* TRANSACTION-SPECIFIC DISCLOSURE MODAL */}
      {transactionDisclosure && <TransactionDisclosureModal {...disclosureModalProps} />}
    </>
  );
}
